import type { Component } from 'vue';
import type { IGridColumnCssRule } from './IGridColumnCssRule';

interface IGridCheckboxSelectionColumnOptions<TRowData, TCellData = any, TCellRendererParams = any> {
    /**
     * The width of the column
     */
    width?: number;

    /**
     * The maximum width of the column
     */
    maxWidth?: number;

    /**
     * The minimum width of the column
     */
    minWidth?: number;

    /**
     * Should the column be resizable
     */
    resizable?: boolean;

    /**
     * Should the column be hidden
     */
    hidden?: boolean;

    /**
     * Should the column be pinned to the left or right
     */
    pinned?: 'left' | 'right';

    /**
     * Should the column be movable
     */
    suppressMovable?: boolean;

    /**
     * Should the column be draggable
     */
    draggable?: boolean | ((params: TRowData) => boolean);

    /**
     * Set the css class of the cell based on the value of the row
     */
    cellClassRules?: IGridColumnCssRule<TRowData, any>[];

    /**
     * This will be used as WatchSource in a watch in the cell renderer.
     * This can be used in conjunction with the cellClassRules to trigger a refresh of the cell styling
     * Since this is a WatchSource the return value should change any time we want the watch to trigger (and thus refresh the cell)
     * The best thing to do here is to return an object containing all the dependencies that should trigger a refresh.
     * TODO: This should be replaced with a complete override of the AgGrids builtin cell css class stuff so it can just be reactive on the css rules directly.
     * @param row The grid datarow
     * @param cell The cell data
     * @returns This should return a changed value from the last time it was called if we want to refresh the cell
     */
    cellRefreshTrigger?: (row?: TRowData, cell?: any) => any;

    /**
     * The flex value of the column. This is used to determine how much space the column should take up in the grid
     */
    flex?: number;

    /**
     * The vue component used to render the cell
     */
    cellRenderer?: Component<TCellRendererParams>;

    /**
     * The parameters for the cell renderer component
     */
    cellRendererParams?: TCellRendererParams;

    /**
     * Callback to get the a value from the row used as the cell value for the checkbox column.
     * This will be passed on to the cellrenderer
     * @param params
     * @returns
     */
    valueGetter?: (params: TRowData) => TCellData;

    // /**
    //  * The vue component used to render the header
    //  */
    // headerComponent?: Component | string; // TODO: Typer

    // /**
    //  * The parameters for the header component
    //  */
    // headerComponentParams?: any; // TODO: Typer

    // /**
    //  * Overrideable headerValueGetter function.
    //  */
    // headerValueGetter?: (headerText: string) => string;
}

/**
 * This class is used to define the options for the checkbox selection column in the grid
 * Note: This could have a common ancestor with GridColumnOptions, but I'm not sure that gives us any value
 *
 * TODO: This can have a headerComponent
 *
 * TODO: If we set a renderComponent we get a build error if we dont include the value in the props, even tho the value is supplied by the AgCellRendererWrapper,
 * essentially there is something wrong with the type definitions here.
 * The TCellRendererParams could Omit the value since its always supplied, only its not always supplied. We need to have a valueGetter for there to be a value
 */
export default class GridCheckboxSelectionColumnOptions<TRowData, TCellData = any, TCellRendererParams = any> {
    public width?: number;
    public maxWidth?: number;
    public minWidth?: number;
    public resizable?: boolean;
    public pinned?: 'left' | 'right';
    public suppressMovable?: boolean;
    public draggable?: boolean | ((params: TRowData) => boolean);
    public cellRenderer?: Component<TCellRendererParams>;
    public cellRendererParams?: TCellRendererParams;
    public cellClassRules?: IGridColumnCssRule<TRowData, any>[];
    public valueGetter?: (params: TRowData) => any;
    public cellRefreshTrigger?: (row?: TRowData, cell?: any) => any;
    public flex?: number;

    public constructor(columnOptions: IGridCheckboxSelectionColumnOptions<TRowData, TCellData, TCellRendererParams>) {
        this.width = columnOptions.width;
        this.maxWidth = columnOptions.maxWidth;
        this.minWidth = columnOptions.minWidth;
        this.resizable = columnOptions.resizable ?? true;
        this.suppressMovable = columnOptions.suppressMovable ?? false;
        this.pinned = columnOptions.pinned;
        this.draggable = columnOptions.draggable;
        this.cellRenderer = columnOptions.cellRenderer;
        this.cellRendererParams = columnOptions.cellRendererParams;
        this.flex = columnOptions.flex;
        this.cellClassRules = columnOptions.cellClassRules;
        this.valueGetter = columnOptions.valueGetter;
        this.cellRefreshTrigger = columnOptions.cellRefreshTrigger;
    }
}
